import { z } from 'zod';

import { createCrmClient, type CrmClient } from '@zep/crm/quotes';

import { type QuizQuoteFormValues } from '../../utils/quizQuote.schema';
import { quoteDateSchema } from '../../utils/quoteDate';
import { formatKoreanAmount } from '../../utils/quoteFormat';

import { type PublicQuizQuote, toPublicQuizQuote } from './publicQuizQuote';

const amountSchema = z.number().finite().nonnegative();
const discountSchema = z.object({
  code: z.string().min(1),
  label: z.string().min(1),
  rate: z.number().finite().min(0).max(1).nullable().optional(),
  amount: amountSchema,
});
const pricingTypeNoticeSchema = z
  .object({
    code: z.string().min(1),
    message: z.string().min(1),
  })
  .nullish();

const issuedQuizQuoteSchema = z
  .object({
    recipient: z.object({
      organizationName: z.string().min(1),
      contactName: z.string().min(1),
      phone: z.string().min(1),
      email: z.string().email(),
      annotation: z.string().optional(),
    }),
    product: z.object({
      plan: z.enum(['basic', 'pro']),
      requestedPricingType: z.enum(['individual', 'group']),
      pricingType: z.enum(['individual', 'group']),
      pricingTypeNotice: pricingTypeNoticeSchema,
      userCount: z.number().int().positive(),
      monthCount: z.number().int().positive(),
      startDate: quoteDateSchema,
      endDate: quoteDateSchema,
    }),
    pricing: z.object({
      currency: z.literal('KRW'),
      regularUnitAmount: amountSchema,
      originalAmount: amountSchema,
      discountAmount: amountSchema,
      finalAmount: amountSchema,
      discounts: z.array(discountSchema).max(1),
    }),
    issuedAt: z.string().datetime(),
    validUntil: z.string().datetime(),
  })
  .superRefine(({ product, pricing }, ctx) => {
    if (
      product.requestedPricingType !== product.pricingType &&
      !product.pricingTypeNotice
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product', 'pricingTypeNotice'],
        message: '적용 요금 유형이 변경되면 안내가 필요합니다.',
      });
    }

    if (
      pricing.regularUnitAmount * product.userCount * product.monthCount !==
      pricing.originalAmount
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'originalAmount'],
        message: '견적 정가가 단가, 인원, 기간과 일치하지 않습니다.',
      });
    }

    if (
      pricing.originalAmount - pricing.discountAmount !==
      pricing.finalAmount
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'finalAmount'],
        message: '견적 금액 합계가 일치하지 않습니다.',
      });
    }

    const appliedDiscountAmount = pricing.discounts.reduce(
      (sum, discount) => sum + discount.amount,
      0,
    );

    if (appliedDiscountAmount !== pricing.discountAmount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pricing', 'discounts'],
        message: '할인 상세와 총 할인 금액이 일치하지 않습니다.',
      });
    }

    if (product.endDate !== addMonths(product.startDate, product.monthCount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product', 'endDate'],
        message: '견적 종료일이 시작일과 기간에 일치하지 않습니다.',
      });
    }
  });

export type IssuedQuizQuote = z.infer<typeof issuedQuizQuoteSchema>;

export const QUOTE_UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type QuoteUtmParams = Partial<
  Record<(typeof QUOTE_UTM_KEYS)[number], string>
>;

/**
 * CRM 견적 접수(`POST /api/crm/v1/quotes/quiz`) 요청 shape.
 * CRM 필수: accountName·accountEmail·accountPhone·plan·userCount·startDate·endDate.
 * `planType`·`term`·`quotePolicy` 는 CRM 이 raw payload 로 원장에 보존한다(영업 참고용).
 */
export interface QuizQuoteApiRequest {
  schoolName: string;
  accountName: string;
  accountPhone: string;
  accountEmail: string;
  plan: 'basic' | 'pro';
  planType: 'individual' | 'group';
  userCount: number;
  term: number;
  startDate: string;
  endDate?: string;
  finalAmount?: number;
  finalAmountKr?: string;
  annotation?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  quotePolicy?: {
    requestedPricingType: 'individual' | 'group';
    pricingType: 'individual' | 'group';
    appliedDiscounts: { code: string; label: string }[];
    pricingTypeNotice: { code: string; message: string } | null;
  };
  /** 고객이 토큰 링크로 열 견적서 원본. CRM 은 보관만 하고 해석하지 않는다. */
  document?: PublicQuizQuote;
  /** 견적서 화면 경로. CRM 이 제출 오리진 + 이 경로 + 토큰으로 메일 링크를 만든다. */
  viewPath?: string;
}

/** CRM 견적 접수 전용 클라이언트. 사용자에게 보일 문구는 견적 도메인이 정한다. */
/**
 * 패키지는 앱 env 이름을 모르므로 주소 결정만 여기서 한다. 로컬(LOC)은 next.config
 * 의 `/crm/*` → dev-crm 프록시를 타고, 그 외는 NEXT_PUBLIC_CRM_URL 절대 주소를 쓴다.
 */
export const quizQuoteCrmClient = createCrmClient({
  messages: {
    notConfigured:
      '견적서 발행 서버 설정이 없습니다. 잠시 후 다시 시도해 주세요.',
    failed: '견적서 발행에 실패했습니다.',
  },
  resolveBaseUrl: () => process.env.NEXT_PUBLIC_CRM_URL,
  resolveUrl:
    process.env.NEXT_PUBLIC_STAGE === 'LOC' ? path => `/crm${path}` : undefined,
});

function addMonths(date: string, monthCount: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const targetMonth = month + monthCount;
  const targetYear = year + Math.floor((targetMonth - 1) / 12);
  const normalizedMonth = ((targetMonth - 1) % 12) + 1;
  const lastDay = new Date(
    Date.UTC(targetYear, normalizedMonth, 0),
  ).getUTCDate();
  const targetDay = Math.min(day, lastDay);

  return `${targetYear.toString().padStart(4, '0')}-${normalizedMonth
    .toString()
    .padStart(2, '0')}-${targetDay.toString().padStart(2, '0')}`;
}

export function parseIssuedQuizQuote(value: unknown): IssuedQuizQuote {
  return issuedQuizQuoteSchema.parse(value);
}

/**
 * 클라이언트가 만든 견적서를 CRM 요청 shape 으로 옮긴다.
 *
 * `planType` 은 **사용자가 요청한** 유형, 실제 적용 유형은 `quotePolicy.pricingType` 이다.
 * CRM 은 금액을 계산하지 않고 받은 값을 원장·알림(이메일/알림톡)에 그대로 쓴다.
 */
export function toQuizQuoteNotificationRequest(
  values: QuizQuoteFormValues,
  utm: QuoteUtmParams,
  quote: IssuedQuizQuote,
): QuizQuoteApiRequest {
  const { pricing, product } = quote;
  // CRM 은 finalAmount 가 있으면 양수만 받는다 — 0원 견적은 생략(선택 필드).
  const hasAmount = pricing.finalAmount > 0;

  return {
    schoolName: values.school_name,
    accountName: values.account_name,
    accountPhone: values.account_phone,
    accountEmail: values.account_email,
    plan: values.plan,
    planType: values.plan_type,
    userCount: values.user_count,
    term: values.term,
    startDate: values.start_date,
    endDate: product.endDate,
    finalAmount: hasAmount ? pricing.finalAmount : undefined,
    finalAmountKr: hasAmount
      ? formatKoreanAmount(pricing.finalAmount)
      : undefined,
    annotation: values.annotation || undefined,
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmContent: utm.utm_content,
    utmTerm: utm.utm_term,
    document: toPublicQuizQuote(quote),
    viewPath: QUOTE_VIEW_PATH,
    quotePolicy: {
      requestedPricingType: product.requestedPricingType,
      pricingType: product.pricingType,
      appliedDiscounts: pricing.discounts.map(({ code, label }) => ({
        code,
        label,
      })),
      pricingTypeNotice: product.pricingTypeNotice ?? null,
    },
  };
}

const CRM_QUOTE_PATH = '/api/crm/v1/quotes/quiz';

/** 토큰으로 견적서를 여는 화면(`/pricing/quote/view/[token]`). */
export const QUOTE_VIEW_PATH = '/pricing/quote/view';

/**
 * 견적 신청을 CRM 에 접수한다. 응답은 쓰지 않는다 — 견적서는 클라가
 * `createQuizQuote()` 로 이미 확정했다.
 *
 * `crm` 은 스토리·테스트가 가짜 클라이언트를 넣기 위한 자리다.
 */
export async function sendQuizQuoteNotification(
  request: QuizQuoteApiRequest,
  crm: CrmClient = quizQuoteCrmClient,
): Promise<void> {
  await crm.post(CRM_QUOTE_PATH, request);
}
